import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Eye, Calendar, Search, PlusCircle, Pencil, Loader2, ListChecks } from 'lucide-react';
import { useVendorInvoices } from '../../hooks/useVendorInvoices';
import { useVendorProfile } from '../../services/vendorProfileService';
import { vendorInvoiceApi } from '../../services/vendorInvoiceService';
import { downloadInvoicePdfFromInvoiceData } from '../../utils/vendorInvoicePdf';
import { useToastContext } from '../../contexts/ToastContext';
import {
  VendorPageHeader,
  VendorGuideCallout,
  VendorEmptyState,
} from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const invoiceStatusColor = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'paid':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
    case 'sent':
    case 'pending':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
    case 'overdue':
      return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300';
    case 'draft':
    case 'cancelled':
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
};

const VendorInvoices = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const { error: showErrorToast } = useToastContext();
  const { data: profileResp } = useVendorProfile();

  const {
    invoices,
    isLoading,
    error,
    pagination,
    formatAmount,
    formatDate,
  } = useVendorInvoices(currentPage, 15, {
    search: searchTerm,
  });

  const handleDownload = useCallback(
    async (invoiceId) => {
      setDownloadingId(invoiceId);
      try {
        const res = await vendorInvoiceApi.getOne(invoiceId);
        const inv = res?.data;
        const vp = profileResp?.data ?? {};
        if (inv) await downloadInvoicePdfFromInvoiceData(inv, vp);
      } catch {
        showErrorToast('Download failed', 'Could not download invoice. Try again.');
      } finally {
        setDownloadingId(null);
      }
    },
    [profileResp, showErrorToast]
  );

  const canEditInvoice = (inv) =>
    inv?.status && !['paid', 'cancelled'].includes(String(inv.status).toLowerCase());

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Invoices"
        subtitle="Create, send, and download invoices — with logos, discounts, tax, and PDF export."
        actions={
          <Link
            to="/vendor/invoices/create"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium !rounded-xl shadow-sm bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 w-full sm:w-auto"
          >
            <PlusCircle className="w-4 h-4" />
            Custom invoice
          </Link>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.invoices.title}
        steps={VENDOR_PAGE_GUIDES.invoices.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading invoices…</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-300 px-4 py-3 text-sm">
              Failed to load invoices. Please try again.
            </div>
          ) : invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4 sm:p-5 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 min-w-0 flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-400">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-gray-50">
                          Invoice #{invoice.invoice_number || invoice.id}
                        </p>
                        {invoice.client_name && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {invoice.client_name}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            {formatDate(invoice.invoice_date || invoice.created_at)}
                          </span>
                          {invoice.total_amount != null && (
                            <span className="font-semibold text-gray-900 dark:text-gray-50 tabular-nums">
                              ${formatAmount(invoice.total_amount)}
                            </span>
                          )}
                          {invoice.status && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${invoiceStatusColor(invoice.status)}`}
                            >
                              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:pl-12">
                      <Link
                        to={`/vendor/invoices/${invoice.id}`}
                        className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 !rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">View</span>
                      </Link>
                      {canEditInvoice(invoice) && (
                        <Link
                          to={`/vendor/invoices/${invoice.id}/edit`}
                          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 !rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
                        >
                          <Pencil className="w-4 h-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDownload(invoice.id)}
                        disabled={downloadingId === invoice.id}
                        className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-black dark:bg-gray-100 text-white dark:text-gray-900 !rounded-xl hover:bg-gray-800 dark:hover:bg-white transition-colors text-sm disabled:opacity-60"
                      >
                        {downloadingId === invoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">Download</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <VendorEmptyState
              icon={FileText}
              title={searchTerm ? 'No invoices match your search' : 'No invoices yet'}
              description={
                searchTerm
                  ? 'Try a different search term.'
                  : 'Create a custom invoice for a client, or generate one from a booking when you’re ready to bill.'
              }
              tips={
                searchTerm
                  ? []
                  : [
                      'Add your business logo in Account → Business',
                      'Include discounts and tax when needed',
                      'Email or download a PDF after saving',
                    ]
              }
              actionLabel={searchTerm ? undefined : 'Create custom invoice'}
              actionTo={searchTerm ? undefined : '/vendor/invoices/create'}
            />
          )}
        </div>

        {pagination && pagination.last_page > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={pagination.current_page === 1}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 !rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let page;
                  if (pagination.last_page <= 5) {
                    page = i + 1;
                  } else if (pagination.current_page <= 3) {
                    page = i + 1;
                  } else if (pagination.current_page >= pagination.last_page - 2) {
                    page = pagination.last_page - 4 + i;
                  } else {
                    page = pagination.current_page - 2 + i;
                  }

                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm border !rounded-xl transition-colors ${
                        pagination.current_page === page
                          ? 'bg-black dark:bg-gray-100 text-white dark:text-gray-900 border-black dark:border-gray-100'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(pagination.last_page, prev + 1))}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 !rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorInvoices;
